import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import MapFeedbackGeometry from '../components/MapFeedbackGeometry';
import { fetchFeedback, saveTriage } from '../services/mapFeedbackService';
import { fetchSkiAreas } from '../services/skiAreasService';
import { useError } from '../contexts/ErrorContext';
import './MapFeedbackPage.css';

const STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'done', label: 'Done' },
];

// Deliberately colourless. Status is the only axis that carries colour in this
// list, so a "new run" cannot render blue for its type and blue for its status
// at the same time. The badge label is what distinguishes a type here; the
// detail map still colours run/lift/freeform, where hue separates overlapping
// geometry and cannot collide with status.
const TYPES = [
  { value: 'run', label: 'Run' },
  { value: 'lift', label: 'Lift' },
  { value: 'freeform', label: 'Freeform' },
];

const TYPE_META = Object.fromEntries(TYPES.map(t => [t.value, t]));

const DEFAULT_DAYS = 90;

const isoDaysAgo = (days) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
};

const formatWhen = (iso) => {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * Render a value for the current/suggested columns.
 * Numbers are rounded — a suggested length of 3000.0000001 is noise.
 */
const formatValue = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(1);
  return String(value);
};

/**
 * One flagged field: what the map says now, and what the user says it should be.
 *
 * Three cases have to stay visually distinct, because they mean different
 * things to an operator:
 *   - a suggestion            → act on it
 *   - flagged, no suggestion  → the field is wrong but the user didn't say how
 *   - value not in the map DB → access and grooming live in the status feed
 */
const FieldRow = ({ field }) => {
  const current = formatValue(field.current);
  const suggested = formatValue(field.suggested);

  return (
    <tr>
      <th scope="row">{field.label}</th>
      <td className="mfb-current">
        {!field.currentAvailable
          ? <span className="mfb-na">not stored in the map database</span>
          : current ?? <span className="mfb-na">—</span>}
      </td>
      <td className="mfb-arrow" aria-hidden="true">→</td>
      <td className="mfb-suggested">
        {suggested
          ? <strong>{suggested}</strong>
          : <span className="mfb-unanswered">flagged, no value given</span>}
      </td>
    </tr>
  );
};

/** The checkbox-only reasons on a freeform report. */
const ReasonList = ({ fields }) => (
  <ul className="mfb-reasons">
    {fields.map(field => <li key={field.key}>{field.label}</li>)}
  </ul>
);

const FREEFORM_GROUP = '__freeform__';

/**
 * Group reports by the resort they concern, most recently reported first.
 *
 * Run and lift reports name their ski area outright, so they group on it.
 * Freeform reports do not — they carry only a coordinate — so they all collect
 * in one Freeform group instead of being filed under whichever resort happened
 * to be nearest. A 0.2 km match and a 24 km match would otherwise sit in the
 * header looking equally certain, and a resort could get a group it was never
 * actually named in. The nearest-resort match still appears on each freeform
 * card, where the distance is visible next to it.
 *
 * @param {object[]} reports
 * @returns {object[]} { id, name, skiAreaId, latest, reports, newCount }
 */
const groupByResort = (reports) => {
  const groups = new Map();

  for (const report of reports) {
    const isFreeform = report.type === 'freeform';
    const key = isFreeform ? FREEFORM_GROUP : (report.skiAreaId || FREEFORM_GROUP);

    if (!groups.has(key)) {
      groups.set(key, {
        id: key,
        skiAreaId: isFreeform ? null : report.skiAreaId,
        name: isFreeform ? 'Freeform reports' : (report.skiAreaName || report.skiAreaId),
        isFreeform,
        latest: report.timestamp,
        reports: [],
        newCount: 0,
      });
    }

    const group = groups.get(key);
    group.reports.push(report);
    if (report.triage.status === 'new') group.newCount += 1;
    // Reports arrive newest-first, but a filter or a status change must not be
    // able to leave a stale date on the header.
    if (!group.latest || report.timestamp > group.latest) group.latest = report.timestamp;
  }

  return [...groups.values()]
    .sort((a, b) => (b.latest || '').localeCompare(a.latest || ''));
};

/** One resort, collapsed to a summary until opened. */
const ResortGroup = ({ group, open, onToggle, onTriaged }) => (
  <li className={`mfb-group${open ? ' open' : ''}`}>
    <button type="button" className="mfb-group-head" onClick={onToggle}>
      <span className="mfb-group-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
      <span className="mfb-group-name">{group.name}</span>
      {group.skiAreaId && <code className="mfb-id">{group.skiAreaId}</code>}
      <span className="mfb-group-count">
        {group.reports.length} report{group.reports.length === 1 ? '' : 's'}
      </span>
      {group.newCount > 0 && (
        <span className="mfb-chip mfb-chip-new">{group.newCount} new</span>
      )}
      <span className="mfb-group-latest">latest {formatWhen(group.latest)}</span>
    </button>

    {open && (
      <ul className="mfb-list">
        {group.reports.map(report => (
          <ReportCard key={report.reportId} report={report} onTriaged={onTriaged} />
        ))}
      </ul>
    )}
  </li>
);

const ReportCard = ({ report, onTriaged }) => {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(report.triage.notes || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const type = TYPE_META[report.type];
  const notesDirty = notes !== (report.triage.notes || '');

  const persist = useCallback(async (patch) => {
    setSaving(true);
    setSaveError(null);
    try {
      const { triage } = await saveTriage(report.reportId, patch);
      onTriaged(report.reportId, triage);
      setSavedAt(Date.now());
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }, [report.reportId, onTriaged]);

  const title = report.type === 'freeform'
    ? 'Freeform report'
    : (report.feature?.name || report.featureNameAtReport || report.featureId || 'Unknown feature');

  return (
    <li className={`mfb-card mfb-status-${report.triage.status}`}>
      <div className="mfb-card-head">
        <span className="mfb-badge">{type.label}</span>
        <span className="mfb-title">{title}</span>
        {report.featureId && <code className="mfb-id">{report.featureId}</code>}
        <span className="mfb-when">{formatWhen(report.timestamp)}</span>
      </div>

      <div className="mfb-meta">
        {/* Run and lift cards sit under a header that already names the
            resort; freeform cards do not, so they name their nearest match. */}
        {report.skiAreaMissing && (
          <span className="mfb-warn">
            ski area <code>{report.skiAreaId}</code> is not in the map database
          </span>
        )}
        {report.nearestSkiArea && (
          <span>
            nearest resort: {report.nearestSkiArea.name}
            {' '}({report.nearestSkiArea.distanceKm} km away)
          </span>
        )}
        {report.type === 'freeform' && !report.nearestSkiArea && (
          <span className="mfb-warn">no resort within 25 km of this point</span>
        )}
        <span>{report.userName || report.userId || report.userPseudoId || 'unknown user'}</span>
        {report.platform && <span>{report.platform}</span>}
        {report.gogglesSwVersion && <span>goggles {report.gogglesSwVersion}</span>}
      </div>

      {report.featureMissing && report.type !== 'freeform' && (
        <p className="mfb-notice">
          This {report.type} is no longer in the map database
          {report.featureNameAtReport ? ` (it was “${report.featureNameAtReport}”)` : ''}.
          It was probably renamed or removed by a later import, so the report may
          already be handled.
        </p>
      )}

      {report.featureRenamed && (
        <p className="mfb-notice">
          Called “{report.featureNameAtReport}” when this was reported, now
          “{report.feature.name}”.
        </p>
      )}

      {report.type === 'freeform'
        ? (
          <>
            {report.fields.length > 0 && <ReasonList fields={report.fields} />}
            {report.latitude != null && (
              <p className="mfb-coords">
                {report.latitude.toFixed(5)}, {report.longitude.toFixed(5)}
              </p>
            )}
          </>
        )
        : report.fields.length > 0 && (
          <table className="mfb-fields">
            <thead>
              <tr>
                <th scope="col">Field</th>
                <th scope="col">Current</th>
                <th scope="col" aria-hidden="true" />
                <th scope="col">Suggested</th>
              </tr>
            </thead>
            <tbody>
              {report.fields.map(field => <FieldRow key={field.key} field={field} />)}
            </tbody>
          </table>
        )}

      {report.freeText && (
        <blockquote className="mfb-freetext">{report.freeText}</blockquote>
      )}

      {report.flaggedSomethingElse && !report.freeText && (
        <p className="mfb-notice">
          The user ticked “Something else” but left the description empty.
        </p>
      )}

      <div className="mfb-actions">
        <div className="mfb-statuses">
          {STATUSES.map(s => (
            <button
              key={s.value}
              type="button"
              className={`mfb-status-btn${report.triage.status === s.value ? ' active' : ''}`}
              disabled={saving}
              onClick={() => persist({ status: s.value })}
            >
              {s.label}
            </button>
          ))}
        </div>
        <Button
          variant="secondary"
          size="small"
          onClick={() => setExpanded(e => !e)}
        >
          {expanded ? 'Hide map' : 'Show on map'}
        </Button>
      </div>

      <div className="mfb-notes">
        <textarea
          value={notes}
          placeholder="Triage notes…"
          rows={2}
          onChange={(e) => setNotes(e.target.value)}
        />
        <div className="mfb-notes-foot">
          {report.triage.updatedBy && (
            <span className="mfb-updated">
              last set by {report.triage.updatedBy} {formatWhen(report.triage.updatedAt)}
            </span>
          )}
          {saveError && <span className="mfb-save-error">{saveError}</span>}
          {!saveError && savedAt && !notesDirty && <span className="mfb-saved">Saved</span>}
          <Button
            variant="primary"
            size="small"
            disabled={!notesDirty || saving}
            onClick={() => persist({ notes })}
          >
            Save notes
          </Button>
        </div>
      </div>

      {expanded && <MapFeedbackGeometry report={report} />}
    </li>
  );
};

const MapFeedbackPage = () => {
  const navigate = useNavigate();
  const { addError } = useError();

  const [startDate, setStartDate] = useState(isoDaysAgo(DEFAULT_DAYS));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [skiAreaFilter, setSkiAreaFilter] = useState('');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [skiAreas, setSkiAreas] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFeedback({
        startDate, endDate, type: typeFilter, status: statusFilter, skiAreaId: skiAreaFilter,
      });
      setData(result);
    } catch (err) {
      setError(err.message);
      addError(err.message, err.stack, 'User Feedback');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, typeFilter, statusFilter, skiAreaFilter, addError]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // The dropdown is a convenience; a failure here must not block the reports.
    fetchSkiAreas().then(areas => setSkiAreas(areas || [])).catch(() => setSkiAreas([]));
  }, []);

  const onTriaged = useCallback((reportId, triage) => {
    setData(prev => (prev ? {
      ...prev,
      reports: prev.reports.map(r => (r.reportId === reportId ? { ...r, triage } : r)),
    } : prev));
  }, []);

  // Recomputed locally so the counts follow an in-place status change without a
  // refetch; the server sends the same shape for the first render.
  const counts = useMemo(() => {
    const out = { byStatus: {}, byType: {} };
    for (const r of data?.reports || []) {
      out.byStatus[r.triage.status] = (out.byStatus[r.triage.status] || 0) + 1;
      out.byType[r.type] = (out.byType[r.type] || 0) + 1;
    }
    return out;
  }, [data]);

  const reports = useMemo(() => data?.reports || [], [data]);
  const groups = useMemo(() => groupByResort(reports), [reports]);
  const resortCount = groups.filter(g => !g.isFreeform).length;

  const [openGroups, setOpenGroups] = useState(() => new Set());
  const toggleGroup = useCallback((id) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // A single resort is not a list worth collapsing — open it straight away.
  useEffect(() => {
    if (groups.length === 1) setOpenGroups(new Set([groups[0].id]));
  }, [groups]);

  const allOpen = groups.length > 0 && groups.every(g => openGroups.has(g.id));
  const toggleAll = useCallback(() => {
    setOpenGroups(allOpen ? new Set() : new Set(groups.map(g => g.id)));
  }, [allOpen, groups]);

  return (
    <div className="mfb-page">
      <div className="mfb-header">
        <button type="button" className="mfb-back" onClick={() => navigate('/map-management')}>
          ← Map Management
        </button>
        <h2>User Feedback</h2>
        <p className="mfb-sub">
          Map problems reported from the phone, joined to the run or lift they are
          about. Reports arrive through Firebase Analytics streaming export, so a
          report submitted a few minutes ago is already here.
        </p>
      </div>

      <div className="mfb-controls">
        <label>
          From
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </label>
        <label>
          To
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </label>
        <label>
          Type
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">All</option>
            {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        <label>
          Status
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <label>
          Ski area
          <select value={skiAreaFilter} onChange={e => setSkiAreaFilter(e.target.value)}>
            <option value="">All</option>
            {skiAreas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        <Button variant="secondary" size="small" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {error && <div className="mfb-error">{error}</div>}

      {!error && !loading && (
        <div className="mfb-summary">
          <span className="mfb-summary-total">
            {reports.length} report{reports.length === 1 ? '' : 's'}
            {resortCount > 0 && (
              // The freeform group is not a resort, so it is not counted as one.
              `${' across '}${resortCount} resort${resortCount === 1 ? '' : 's'}`
            )}
          </span>
          {STATUSES.filter(s => counts.byStatus[s.value]).map(s => (
            <span key={s.value} className={`mfb-chip mfb-chip-${s.value}`}>
              {counts.byStatus[s.value]} {s.label.toLowerCase()}
            </span>
          ))}
          {TYPES.filter(t => counts.byType[t.value]).map(t => (
            <span key={t.value} className="mfb-chip">
              {counts.byType[t.value]} {t.label.toLowerCase()}
            </span>
          ))}
          {groups.length > 1 && (
            <button type="button" className="mfb-toggle-all" onClick={toggleAll}>
              {allOpen ? 'Collapse all' : 'Expand all'}
            </button>
          )}
        </div>
      )}

      {loading && <div className="mfb-loading">Loading reports…</div>}

      {!loading && !error && reports.length === 0 && (
        <div className="mfb-empty">
          <p>No reports in this range.</p>
          <p>
            Reports come from the three “Improve this map” flows in the phone app.
            Volume is genuinely low — widen the date range before assuming
            something is broken.
          </p>
        </div>
      )}

      {!loading && groups.length > 0 && (
        <ul className="mfb-groups">
          {groups.map(group => (
            <ResortGroup
              key={group.id}
              group={group}
              open={openGroups.has(group.id)}
              onToggle={() => toggleGroup(group.id)}
              onTriaged={onTriaged}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default MapFeedbackPage;
